import React, { useState } from "react";
import { Outlet, NavLink, useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useSettings } from "@/context/SettingsContext";
import { LayoutDashboard, Boxes, Inbox, Images, Warehouse, Ship, Building2, MapPin, BarChart3, LogOut, Menu, X, ExternalLink } from "lucide-react";

const NAV = [
  { to: "/admin/dashboard", label: "Dashboard", icon: LayoutDashboard },
  { to: "/admin/inventory", label: "Inventory", icon: Boxes },
  { to: "/admin/rfq", label: "RFQs", icon: Inbox },
  { to: "/admin/gallery", label: "Gallery", icon: Images },
  { to: "/admin/warehouse", label: "Warehouse", icon: Warehouse },
  { to: "/admin/export-journey", label: "Export Journey", icon: Ship },
  { to: "/admin/origins", label: "Origins", icon: MapPin },
  { to: "/admin/statistics", label: "Statistics", icon: BarChart3 },
  { to: "/admin/company", label: "Company", icon: Building2 },
];

export default function AdminLayout() {
  const { user, logout } = useAuth();
  const { settings } = useSettings();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  const doLogout = async () => { await logout(); navigate("/admin"); };

  return (
    <div className="min-h-screen bg-[#0f1a14] text-offwhite">
      <div className="flex">
        {/* Sidebar */}
        <aside className={`fixed inset-y-0 left-0 z-40 w-64 transform border-r border-offwhite/10 bg-[#0b130e] transition-transform lg:translate-x-0 ${open ? "translate-x-0" : "-translate-x-full"}`}>
          <div className="flex items-center gap-2 border-b border-offwhite/10 px-5 py-4">
            <img src={settings.logo || "/logo.jpg"} alt="" className="h-9 w-9 rounded bg-white p-0.5" />
            <div className="leading-none"><div className="font-display text-sm font-extrabold">WAREHOUSE</div><div className="font-mono text-[9px] uppercase tracking-widest text-offwhite/50">Admin</div></div>
          </div>
          <nav className="flex flex-col gap-1 p-3">
            {NAV.map((n) => (
              <NavLink key={n.to} to={n.to} onClick={() => setOpen(false)} data-testid={`admin-nav-${n.label.toLowerCase().replace(/ /g,'-')}`}
                className={({ isActive }) => `flex items-center gap-3 rounded-lg px-3 py-2.5 font-body text-sm transition-colors ${isActive ? "bg-forest text-offwhite" : "text-offwhite/65 hover:bg-offwhite/5"}`}>
                <n.icon className="h-4 w-4" /> {n.label}
              </NavLink>
            ))}
          </nav>
          <div className="absolute inset-x-0 bottom-0 border-t border-offwhite/10 p-3">
            <Link to="/" target="_blank" className="mb-2 flex items-center gap-2 rounded-lg px-3 py-2 font-body text-xs text-offwhite/55 hover:bg-offwhite/5"><ExternalLink className="h-3.5 w-3.5" /> View Website</Link>
            <button onClick={doLogout} data-testid="admin-logout" className="flex w-full items-center gap-2 rounded-lg px-3 py-2 font-body text-sm text-rose-300 hover:bg-rose-500/10"><LogOut className="h-4 w-4" /> Sign Out</button>
          </div>
        </aside>
        {open && <div className="fixed inset-0 z-30 bg-black/50 lg:hidden" onClick={() => setOpen(false)} />}

        {/* Main */}
        <div className="flex-1 lg:pl-64">
          <header className="sticky top-0 z-20 flex items-center justify-between border-b border-offwhite/10 bg-[#0f1a14]/90 px-4 py-3 backdrop-blur sm:px-6">
            <button className="lg:hidden" onClick={() => setOpen((v) => !v)} data-testid="admin-menu-toggle">{open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}</button>
            <div className="ml-auto flex items-center gap-3">
              {user?.picture && <img src={user.picture} alt="" className="h-8 w-8 rounded-full" />}
              <div className="text-right leading-none"><div className="font-body text-xs font-semibold">{user?.name}</div><div className="font-mono text-[10px] text-offwhite/50">{user?.email}</div></div>
            </div>
          </header>
          <main className="p-4 sm:p-6 lg:p-8"><Outlet /></main>
        </div>
      </div>
    </div>
  );
}
